import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, ArrowDown } from "lucide-react";

const EASE = [0.83, 0, 0.17, 1];

// Each word is wrapped in an overflow-hidden mask and animated Y from 100% → 0
function LineMask({ children, delay = 0 }) {
  return (
    <span className="reveal-mask">
      <motion.span
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.15, ease: EASE, delay }}
        className="inline-block"
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-plum pt-32 pb-16 md:pt-40 md:pb-24"
    >
      {/* Ambient background architecture image (parallax) */}
      <motion.div
        style={{ y: imageY }}
        className="absolute inset-0 -z-10"
        aria-hidden
      >
        <div
          className="absolute inset-0 bg-cover bg-center image-duotone opacity-[0.18]"
          style={{
            backgroundImage:
              'url("https://images.unsplash.com/photo-1580587771525-78b9dba3b914?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400")',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-plum/40 via-plum/70 to-plum" />
        <div className="absolute inset-0 bg-gradient-to-r from-plum via-transparent to-plum/60" />
      </motion.div>

      {/* Corner marks */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.4, delay: 2.1 }}
        className="pointer-events-none absolute top-24 md:top-32 right-6 md:right-12 lg:right-16 hidden md:flex items-center gap-3 text-gold/70 text-[10px] tracking-[0.4em] uppercase"
        aria-hidden
      >
        Queensland · National
        <span className="w-6 h-px bg-gold/60" />
      </motion.div>

      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-6 items-end pt-8 md:pt-14">
        {/* Left: kinetic hero copy */}
        <motion.div
          style={{ y: textY }}
          className="lg:col-span-8 relative z-10"
        >
          <div className="flex items-center gap-4 mb-8 md:mb-10">
            <motion.span
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 1.4, ease: EASE, delay: 0.3 }}
              className="block h-px w-14 bg-gold origin-left"
            />
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="text-gold text-[11px] tracking-[0.42em] uppercase font-sans"
            >
              Fatima Abdallah
            </motion.span>
          </div>

          <h1
            data-testid="hero-headline"
            className="font-serif font-light text-cream tracking-tighter leading-[0.88] text-[13vw] sm:text-[10vw] lg:text-[8.4vw] xl:text-[128px]"
          >
            <span className="block">
              <LineMask delay={0.55}>Connecting</LineMask>{" "}
              <LineMask delay={0.68}>People,</LineMask>
            </span>
            <span className="block">
              <LineMask delay={0.82}>
                <em className="italic text-gold font-light">Property</em>
              </LineMask>{" "}
              <LineMask delay={0.9}>and</LineMask>{" "}
              <LineMask delay={0.98}>
                <em className="italic text-gold/90 font-light">Possibility.</em>
              </LineMask>
            </span>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.35, ease: EASE }}
            className="mt-10 md:mt-14 max-w-2xl grid grid-cols-12 gap-6 items-start"
          >
            <div className="col-span-1 pt-2 hidden md:block">
              <span className="block text-gold text-xs tracking-[0.3em]">
                01
              </span>
            </div>
            <div className="col-span-12 md:col-span-11">
              <p className="font-serif italic text-cream/85 text-2xl md:text-3xl leading-snug">
                Where Property Meets Purpose.
              </p>
              <p className="mt-4 text-cream/70 text-base md:text-lg leading-relaxed max-w-xl text-justify hyphens-auto">
                Creating strategic partnerships between property, buyer's
                advocacy and the disability sector, connecting the right
                people with the right opportunities to deliver better housing
                outcomes.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.55, ease: EASE }}
            className="mt-10 md:mt-14 flex flex-wrap items-center gap-5"
          >
            <a
              href="#connect"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-3 px-8 py-4 bg-gold text-plum text-xs uppercase tracking-[0.3em] font-medium hover:bg-cream transition-colors duration-500"
            >
              Let's Connect
              <ArrowUpRight
                size={16}
                className="transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </a>
            <a
              href="#about"
              data-testid="hero-cta-secondary"
              className="group inline-flex items-center gap-3 px-8 py-4 border border-gold/40 text-cream text-xs uppercase tracking-[0.3em] hover:border-gold hover:text-gold transition-colors duration-500"
            >
              Learn More
              <ArrowDown
                size={16}
                className="transition-transform duration-500 group-hover:translate-y-1"
              />
            </a>
          </motion.div>
        </motion.div>

        {/* Right: portrait frame */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.4, delay: 1.1, ease: EASE }}
          className="lg:col-span-4 relative z-10 mt-6 lg:mt-0"
        >
          <div className="relative aspect-[3/4] w-full portrait-frame border border-gold/25 overflow-hidden">
            {/* Real portrait */}
            <img
              src="/fatima-portrait.jpg"
              alt="Fatima Abdallah portrait"
              className="absolute inset-0 w-full h-full object-cover object-[center_25%] image-portrait"
              loading="eager"
            />
            {/* Plum gradient overlay for legibility */}
            <div className="absolute inset-0 bg-gradient-to-b from-plum/10 via-transparent to-plum/60 pointer-events-none" />
            {/* Inner clipped frame */}
            <div className="absolute inset-4 border border-gold/25 pointer-events-none" />
            {/* Corner accents */}
            <span className="absolute top-0 left-0 w-6 h-px bg-gold" />
            <span className="absolute top-0 left-0 w-px h-6 bg-gold" />
            <span className="absolute bottom-0 right-0 w-6 h-px bg-gold" />
            <span className="absolute bottom-0 right-0 w-px h-6 bg-gold" />
            {/* Footer plate */}
            <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between text-[10px] tracking-[0.35em] uppercase text-cream/85">
              <span>Fatima Abdallah</span>
              <span className="text-gold">FYA</span>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-end text-[10px] tracking-[0.28em] uppercase text-cream/60">
            <span className="text-right leading-relaxed">
              Founder · Strategic Property &<br className="hidden sm:block" />{" "}
              Disability Housing Advisor
            </span>
          </div>
        </motion.div>
      </div>

      {/* Bottom ticker */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2, delay: 2 }}
        className="absolute bottom-6 left-0 right-0 px-6 md:px-12 lg:px-16 flex items-center justify-between text-[10px] tracking-[0.35em] uppercase text-cream/50"
      >
        <span className="flex items-center gap-3">
          <span className="w-8 h-px bg-gold/60" /> Scroll to explore
        </span>
        <span className="hidden md:inline">
          Property · Disability Housing · Partnerships
        </span>
      </motion.div>
    </section>
  );
}
